<script setup lang="ts">
import type { Grid } from "@/logic/GridBuilder"
import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import type { KPillTabItem } from "@kaleidoscope-solutions/ks-design-system"
import WarningStateImage from "@/assets/pngs/WarningState.png?w=288&h=358&format=webp&imagetools"
import TableGrid from "@/components/Grid/TableGrid.vue"
import { gridFromCellProperties } from "@/logic/GridBuilder"
import worker from "@/sandbox?worker&url"
import { useFileImportStore, useSettingsStore } from "@/stores"
import { captureError, tokenCallback, useEngineModel } from "@/utils/useEngineModel"
import { EngineError } from "@/utils/useEngineModel/error"
import { ModelSandboxFacade } from "@/utils/useEngineModel/ModelSandboxFacade"
import { useAuth0 } from "@auth0/auth0-vue"

import { KPillTabs } from "@kaleidoscope-solutions/ks-design-system"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"

interface Emits {
  (event: "disableImport", disableImport: boolean): void
}

const emit = defineEmits<Emits>()
const fileImportStore = useFileImportStore()
const settingsStore = useSettingsStore()

// Register the onUnmounted callback before other logic
// otherwise as the calls in this file are blocking, and if you switch tabs
// quickly, it never runs the unMounted callback as it never gets registered.
let sandbox: ModelSandboxFacade | null = new ModelSandboxFacade(settingsStore.logLevel)
onUnmounted(() => {
  if (sandbox) {
    // We must terminate the worker each time, otherwise the web workers
    // persist and are generated every time you navigate to the preview.
    sandbox.terminateWorker()
    sandbox = null
  }
})

const tables = ref<{ name: string; grid: Grid }[]>([])
onMounted(async () => {
  try {
    for (let i = 0; i < (fileImportStore.targets ?? []).length; i++) {
      const target = fileImportStore.targets?.[i]
      const table = await getPreviewTable(target)
      tables.value.push(table)
    }
  } catch (error) {
    captureError(error)
  }
})

const model = useEngineModel()
const activeTable = ref(0)
const previewFailedMessage = ref("")

const importData = fileImportStore.importData

const auth0 = useAuth0()

let isThousandRowLimitErr = computed(() => false)

try {
  await sandbox.initialise(tokenCallback(auth0), worker)

  await sandbox.api.loadModelFromYaml(await model.api.saveModelToYaml())

  // TODO
  // THIS RETURNS UNDEFINED WITH OR WITH ASYNC

  if (fileImportStore.isDatasetTarget) {
    for (const target of importData.targets) {
      await sandbox.api.clearDataset(target)
    }
  } else {
    for (const target of importData.targets) {
      const targetList = await model.primaryListNameForType(target)
      await sandbox.api.clearList(targetList)
    }
  }
  await sandbox.api.import(JSON.stringify(importData))

  previewFailedMessage.value = ""
} catch (error) {
  if (error instanceof EngineError) {
    previewFailedMessage.value = error.messageWithoutCode
  }
  captureError(error)
}

isThousandRowLimitErr = computed(() => {
  return (
    previewFailedMessage.value.match(`only [0-9]+ items for ktype`) !== null
  )
})

watch(
  isThousandRowLimitErr,
  () => {
    emit("disableImport", isThousandRowLimitErr.value)
  },
  {
    immediate: true,
  }
)

async function getPreviewTable(target?: string) {
  if (target === undefined) {
    throw new Error("Target is not selected")
  }

  if (fileImportStore.isCategoryTarget) {
    throw new Error("Category target is not supported")
  }

  if (sandbox === null) {
    throw new Error("Sandbox not instantiated")
  }

  const table = await sandbox.api.importPreviewTable(target)

  return {
    name: table.name,
    grid: gridFromCellProperties(
      JSON.parse(table.cellProperties) as TableCellProperties
    ) as Grid,
  }
}

const tabs = computed<KPillTabItem[]>(() => {
  return tables.value.map((table, tableIndex) => {
    return {
      text: table.name,
      cb: () => {
        activeTable.value = tableIndex
      },
      active: activeTable.value === tableIndex
    }
  })
})
</script>

<template>
  <div class="flex w-full flex-1 flex-col overflow-auto">
    <div
      v-if="isThousandRowLimitErr"
      class="my-2 flex min-h-96 items-center justify-center rounded-md bg-gray-100"
    >
      <div class="flex items-center">
        <div>
          <img
            :src="WarningStateImage"
            class="w-52 px-8"
            alt=""
          >
        </div>
        <div class="w-80 space-y-4">
          <h2 class="text-lg font-bold">
            Invalid Import
          </h2>
          <p>
            We are not able to handle more than 1000 items in a category, but we
            are working on it.
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="tables.length > 0"
      class="flex flex-col gap-6"
    >
      <KPillTabs
        v-if="tabs.length > 1"
        :items="tabs"
        class="left-0 whitespace-nowrap bg-white"
      />
      <template
        v-for="(table, tableIndex) in tables"
        :key="table.name"
      >
        <TableGrid
          v-if="tableIndex === activeTable"
          :name="table.name"
          :grid="(table.grid as Grid)"
        />
      </template>
    </div>
  </div>
</template>
